import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../../core/models/base-state/base-state';
import { getHealthSummary, setGlobalPageSize } from '../../../../../core/store/actions/app.actions';
import { PatientSurgicalHistory } from '../../../models/patient-surgical-history';
import * as surgicalActions from '../actions/patient-surgical-history.actions';

export interface PatientSurgicalHistoriesState extends EntityState<PatientSurgicalHistory> {
  baseState: baseState.BaseState;
  pageSize: number;
  isFullDatasetLoaded: boolean;
  cachePatientId: number | null;
  cacheLoadedAt: number | null;
  isFullDatasetLoading: boolean;
  fullDatasetError: unknown;
}

export const patientSurgicalHistoriesAdapter = createEntityAdapter<PatientSurgicalHistory>({
  selectId: patientSurgicalHistory => patientSurgicalHistory.patientSurgeryId,
});

export const initialState: PatientSurgicalHistoriesState = patientSurgicalHistoriesAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  pageSize: 5,
  isFullDatasetLoaded: false,
  cachePatientId: null,
  cacheLoadedAt: null,
  isFullDatasetLoading: false,
  fullDatasetError: null,
});

export const patientSurgicalHistoriesFeatureKey = 'patient-surgical-histories';

export const patientSurgicalHistoriesReducer = createReducer(
  initialState,
  on(getHealthSummary, (state): PatientSurgicalHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
      fullDatasetError: null,
    };
  }),

  on(surgicalActions.loadPatientSurgicalHistories, (state, { pageNumber }): PatientSurgicalHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber,
      },
      fullDatasetError: null,
    };
  }),

  on(
    surgicalActions.getPatientSurgicalHistoriesSuccess,
    (
      state,
      { patientSurgicalHistories, pageNumber, isAtEndOfData, totalRecordCount }
    ): PatientSurgicalHistoriesState => {
      const nextState =
        pageNumber <= 1
          ? patientSurgicalHistoriesAdapter.setAll(patientSurgicalHistories, state)
          : patientSurgicalHistoriesAdapter.addMany(patientSurgicalHistories, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(surgicalActions.getPatientSurgicalHistoriesFailure, (state, { error }): PatientSurgicalHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      fullDatasetError: null,
    };
  }),

  on(
    surgicalActions.getAllPatientSurgicalHistories,
    (state): PatientSurgicalHistoriesState => ({
      ...state,
      isFullDatasetLoading: true,
      fullDatasetError: null,
    })
  ),

  on(
    surgicalActions.getAllPatientSurgicalHistoriesSuccess,
    (state, { patientSurgicalHistories, patientId, loadedAt }): PatientSurgicalHistoriesState => {
      const nextState = patientSurgicalHistoriesAdapter.setAll(patientSurgicalHistories, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          error: null,
          isLoading: false,
          pageNumber: 1,
          isAtEndOfData: true,
          totalRecordCount: patientSurgicalHistories.length,
        },
        isFullDatasetLoaded: true,
        cachePatientId: patientId,
        cacheLoadedAt: loadedAt,
        isFullDatasetLoading: false,
        fullDatasetError: null,
      };
    }
  ),

  on(
    surgicalActions.getAllPatientSurgicalHistoriesFailure,
    (state, { error }): PatientSurgicalHistoriesState => ({
      ...state,
      isFullDatasetLoading: false,
      fullDatasetError: error,
    })
  ),

  on(
    surgicalActions.invalidateSurgicalHistoryCache,
    (state): PatientSurgicalHistoriesState => ({
      ...state,
      isFullDatasetLoaded: false,
      cachePatientId: null,
      cacheLoadedAt: null,
      isFullDatasetLoading: false,
      fullDatasetError: null,
    })
  ),

  on(
    setGlobalPageSize,
    (state, { pageSize }): PatientSurgicalHistoriesState => ({
      ...state,
      pageSize,
    })
  )
);
